import { MesfModal } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	Button,
	Grid2,
	MenuItem,
	TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type {
	ProductNode,
	StraightenerProduct,
	StraightenerRecipeVersion,
} from "../models/StraightenerRecipe";

export type StartFromMode = "current" | "copy" | "blank";

interface Props {
	open: boolean;
	products: StraightenerProduct[];
	// All products with their recipe versions (for the copy sources).
	nodes: ProductNode[];
	defaultProductId: number | null;
	// Recipe the copy source opens on (the one selected on the screen). When it is
	// not a valid source, the product's default recipe, then its most recent one.
	defaultSourceRecipeId?: number | null;
	// When opened from a product's empty state, the product is fixed (disabled)
	// and the user only chooses the "Start from" source.
	productLocked?: boolean;
	onCreate: (args: {
		productId: number;
		mode: StartFromMode;
		sourceRecipeId: number | null;
	}) => void;
	onCancel: () => void;
}

const productLabel = (p: StraightenerProduct): string =>
	`(${p.ProductTypeName}) ${p.Name}`;

const versionLabel = (v: StraightenerRecipeVersion): string =>
	`${v.Name ? `${v.Name} · ` : ""}v${v.Version}${
		v.Crew ? ` · Crew ${v.Crew}` : ""
	}${v.IsCurrent ? " · default" : ""}`;

interface SourceOption {
	label: string;
	node: ProductNode;
}

export const AddRecipeModal = ({
	open,
	products,
	nodes,
	defaultProductId,
	defaultSourceRecipeId = null,
	productLocked = false,
	onCreate,
	onCancel,
}: Props) => {
	const [productId, setProductId] = useState<number | null>(null);
	const [mode, setMode] = useState<StartFromMode>("current");
	const [sourceProductId, setSourceProductId] = useState<number | null>(null);
	const [sourceRecipeId, setSourceRecipeId] = useState<number | null>(null);

	// Reset when (re)opened.
	useEffect(() => {
		if (open) {
			setProductId(defaultProductId);
			setMode("current");
			setSourceProductId(null);
			setSourceRecipeId(null);
		}
	}, [open, defaultProductId]);

	const selectedProduct = products.find((p) => p.Id === productId) ?? null;

	// Products with at least one active recipe — the only valid copy sources.
	const sourceOptions = useMemo<SourceOption[]>(
		() =>
			nodes
				.filter((n) => n.versions.some((v) => v.IsActive))
				.map((n) => ({
					label: `(${n.productTypeName}) ${n.productName}`,
					node: n,
				})),
		[nodes],
	);

	// When entering copy mode, default the source to the selected product (if it
	// has active recipes).
	useEffect(() => {
		if (mode !== "copy" || sourceProductId != null) return;
		const def = sourceOptions.find((o) => o.node.productSizeId === productId);
		if (def) setSourceProductId(def.node.productSizeId);
	}, [mode, sourceProductId, sourceOptions, productId]);

	const sourceValue =
		sourceOptions.find((o) => o.node.productSizeId === sourceProductId) ?? null;

	const sourceRecipes = useMemo(
		() => (sourceValue?.node.versions ?? []).filter((v) => v.IsActive),
		[sourceValue],
	);

	// Preselect a source recipe, as the Trend Display's Copy From does. The
	// versions come sorted default first, then most recently modified, so the
	// first one is the fallback in the right order.
	useEffect(() => {
		if (mode !== "copy" || sourceRecipeId != null || !sourceRecipes.length)
			return;
		setSourceRecipeId(
			(
				sourceRecipes.find((v) => v.Id === defaultSourceRecipeId) ??
				sourceRecipes[0]
			).Id,
		);
	}, [mode, sourceRecipeId, sourceRecipes, defaultSourceRecipeId]);

	const canCreate =
		!!productId &&
		(mode === "current" ||
			mode === "blank" ||
			(mode === "copy" && !!sourceRecipeId));

	const handleCreate = () => {
		if (!productId) return;
		onCreate({
			productId,
			mode,
			sourceRecipeId: mode === "copy" ? sourceRecipeId : null,
		});
	};

	if (!open) return null;

	return (
		<MesfModal
			id="straightener-add-recipe"
			title="Create recipe"
			open={open}
			handleClose={onCancel}
			maxWidth="sm"
		>
			<MesfModal.Content dividers style={{ padding: "20px 30px" }}>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12 }}>
						<Autocomplete
							options={products}
							value={selectedProduct}
							onChange={(_, p) => setProductId(p?.Id ?? null)}
							getOptionLabel={productLabel}
							isOptionEqualToValue={(a, b) => a.Id === b.Id}
							disabled={productLocked}
							disableClearable={productLocked}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Select Product"
									size="small"
									required
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							)}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<TextField
							select
							fullWidth
							size="small"
							label="Start from"
							value={mode}
							disabled={!productId}
							onChange={(e) => setMode(e.target.value as StartFromMode)}
							slotProps={{ inputLabel: { shrink: true } }}
						>
							<MenuItem value="current">Current line values</MenuItem>
							<MenuItem value="copy">Copy from other recipe</MenuItem>
							<MenuItem value="blank">Blank</MenuItem>
						</TextField>
					</Grid2>

					{mode === "copy" ? (
						<>
							<Grid2 size={{ xs: 12 }}>
								<Autocomplete
									options={sourceOptions}
									value={sourceValue}
									onChange={(_, o) => {
										setSourceProductId(o?.node.productSizeId ?? null);
										setSourceRecipeId(null);
									}}
									getOptionLabel={(o) => o.label}
									isOptionEqualToValue={(a, b) =>
										a.node.productSizeId === b.node.productSizeId
									}
									noOptionsText="No products with active recipes"
									renderInput={(params) => (
										<TextField
											{...params}
											label="Source product"
											size="small"
											required
											slotProps={{ inputLabel: { shrink: true } }}
										/>
									)}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12 }}>
								<TextField
									select
									fullWidth
									size="small"
									label="Source recipe"
									value={sourceRecipeId != null ? String(sourceRecipeId) : ""}
									disabled={!sourceProductId}
									onChange={(e) => setSourceRecipeId(Number(e.target.value))}
									slotProps={{ inputLabel: { shrink: true } }}
									helperText={
										sourceProductId && sourceRecipes.length === 0
											? "This product has no active recipes"
											: undefined
									}
								>
									{sourceRecipes.map((v) => (
										<MenuItem key={v.Id} value={String(v.Id)}>
											{versionLabel(v)}
										</MenuItem>
									))}
								</TextField>
							</Grid2>
						</>
					) : null}
				</Grid2>
			</MesfModal.Content>

			<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 2,
						width: "100%",
					}}
				>
					<Button variant="contained" color="inherit" onClick={onCancel}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						size="large"
						disabled={!canCreate}
						onClick={handleCreate}
						sx={{ px: 5 }}
					>
						Create
					</Button>
				</Box>
			</MesfModal.Actions>
		</MesfModal>
	);
};
